import { ApiFragment, FragmentLockStatus } from '@/types/workbench';
import { useMutation, UseMutationResult } from 'react-query';
import Api from './api';
import { useQuery, UseQueryResult } from 'react-query';

export function useGetLastFragment(): UseMutationResult<
  ApiFragment[],
  ApiFragment,
  { pointer: number; workerId: string; speakerId?: string },
  unknown
> {
  return useMutation(
    async ({
      pointer,
      workerId,
      speakerId,
    }: {
      pointer: number;
      workerId: string;
      speakerId?: string;
    }) => {
      const qs = speakerId ? `?speakerId=${encodeURIComponent(speakerId)}` : '';
      return await Api.get<ApiFragment[]>(
        `${
          process.env.REACT_APP_WORKER_URL as string
        }/api/worker/${workerId}/fragments/${pointer}${qs}`,
      );
    },
  );
}

export function useCreateFragment(
  worker_id: string,
): UseMutationResult<ApiFragment, ApiFragment, ApiFragment, ApiFragment> {
  return useMutation(async (params) => {
    return await Api.post(
      `${process.env.REACT_APP_WORKER_URL as string}/api/worker/${worker_id}/fragments`,
      params,
    );
  });
}

export function useDisableFragment(worker_id: string): UseMutationResult<
  {
    fragmentIds: number[];
  },
  unknown,
  number[],
  unknown
> {
  return useMutation(async (fragmentIds: number[]) => {
    return await Api.post(
      `${process.env.REACT_APP_WORKER_URL as string}/api/worker/${worker_id}/fragments/disable`,
      {
        fragmentIds,
      },
    );
  });
}

export function useLockFragment(
  worker_id: string,
): UseMutationResult<FragmentLockStatus, unknown, FragmentLockStatus, unknown> {
  return useMutation(async (params: FragmentLockStatus) => {
    return await Api.put(
      `${process.env.REACT_APP_WORKER_URL as string}/api/worker/${worker_id}/fragments/lock`,
      params,
    );
  });
}

export function useProduceFragment(worker_id: string): UseMutationResult<
  {
    fragmentIds: number[];
  },
  unknown,
  number[],
  unknown
> {
  return useMutation(async (params: number[]) => {
    return await Api.post(
      `${process.env.REACT_APP_WORKER_URL as string}/api/worker/${worker_id}/fragments/produce`,
      {
        fragmentIds: params,
      },
    );
  });
}

export function useUpdateFragment(
  worker_id: string,
): UseMutationResult<ApiFragment, unknown, ApiFragment, unknown> {
  return useMutation(async (params: ApiFragment) => {
    return await Api.put(
      `${process.env.REACT_APP_WORKER_URL as string}/api/worker/${worker_id}/fragments`,
      params,
    );
  });
}

export function useGetFragments(
  worker_id: string,
  speakerId?: string,
): UseQueryResult<ApiFragment[], unknown> {
  return useQuery(
    ['fragments', worker_id, speakerId ?? null],
    async () => {
      const qs = speakerId ? `?speakerId=${encodeURIComponent(speakerId)}` : '';
      return await Api.get<ApiFragment[]>(
        `${process.env.REACT_APP_WORKER_URL as string}/api/worker/${worker_id}/fragments${qs}`,
      );
    },
    {
      enabled: !!worker_id,
    },
  );
}

export function useDeleteFragment(): UseMutationResult<unknown, unknown, number, unknown> {
  return useMutation(async (fragmentId: number) => {
    await Api.delete(`${process.env.REACT_APP_WORKER_URL as string}/api/fragments/${fragmentId}`);
  });
}

export function useResetFragment(): UseMutationResult<unknown, unknown, number, unknown> {
  return useMutation(async (fragmentId: number) => {
    await Api.post(
      `${process.env.REACT_APP_WORKER_URL as string}/api/fragments/${fragmentId}/reset`,
      {},
    );
  });
}

export function useDeleteSegment(): UseMutationResult<unknown, unknown, string, unknown> {
  return useMutation(async (segmentId: string) => {
    await Api.delete(`${process.env.REACT_APP_WORKER_URL as string}/api/segments/${segmentId}`);
  });
}

export function useResetSegment(): UseMutationResult<unknown, unknown, string, unknown> {
  return useMutation(async (segmentId: string) => {
    await Api.post(
      `${process.env.REACT_APP_WORKER_URL as string}/api/segments/${segmentId}/reset`,
      {},
    );
  });
}

export function useDeleteProject(): UseMutationResult<unknown, unknown, string, unknown> {
  return useMutation(async (projectId: string) => {
    await Api.delete(`${process.env.REACT_APP_WORKER_URL as string}/api/projects/${projectId}`);
  });
}
